import { CommonModule, registerLocaleData } from '@angular/common';
// Angular
import { BrowserModule } from '@angular/platform-browser';
import { RecargaAposDeployErrorHandler } from '@core/erros/recarga-apos-deploy';
import {
  APP_INITIALIZER,
  ApplicationRef,
  DEFAULT_CURRENCY_CODE,
  DoBootstrap,
  ErrorHandler,
  Injector,
  LOCALE_ID,
  NgModule,
  inject,
} from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxPermissionsModule } from 'ngx-permissions';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LayoutModule } from './layout/layout.module';
import { PartialsModule } from './views/partials/partials.module';
import {
  DataTableService,
  LayoutConfigService,
  LayoutRefService,
  MenuAsideService,
  MenuConfigService,
  MenuHorizontalService,
  PageConfigService,
  SplashScreenService,
  SubheaderService,
} from '@core/_base/layout';
import { HttpUtilsService, LayoutUtilsService, TypesUtilsService } from '@core/_base/crud';
import { LayoutConfig } from '@core/_config/layout.config';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import xml from 'highlight.js/lib/languages/xml';
import json from 'highlight.js/lib/languages/json';
import scss from 'highlight.js/lib/languages/scss';
import typescript from 'highlight.js/lib/languages/typescript';
import { ApiInterceptor } from '@core/services/api/interceptor';
import { ApiService } from '@core/services/api';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import localeBr from '@angular/common/locales/pt';
import { OverlayModule } from '@angular/cdk/overlay';
import { AuthService } from '@core/services/common';
import { Observable, tap } from 'rxjs';
import { GetCurrentSessionResult } from '@core/model/common';
import { EmptyComponentComponent } from './empty-component/empty-component.component';
import { MAT_FAB_DEFAULT_OPTIONS, MatFabDefaultOptions } from '@angular/material/button';
import { NotificationService } from '@core/services/notification.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { TETO_DE_DIALOGO } from '@core/dialogo/largura-de-dialogo';
import { RetryInterceptor } from '@core/services/api/retry-interceptor';
import { setAppInjector } from './app-injector';
import { provideAvisoDeRotaBloqueada } from '@core/services/common/guards/aviso-de-rota-bloqueada';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

registerLocaleData(localeBr, 'pt');

export function appInitializer(
  authService: AuthService,
  appConfig: LayoutConfigService
): () => Observable<GetCurrentSessionResult> {
  return () =>
    authService.getUserByToken().pipe(
      tap(() => {
        if (!appConfig.getConfig()) {
          appConfig.loadConfigs(new LayoutConfig().configs);
        }
      })
    );
}

/**
 * Import specific languages to avoid importing everything
 * The following will lazy load highlight.js core script (~9.6KB) + the selected languages bundle (each lang. ~1kb)
 */
export function getHighlightLanguages() {
  return [
    { name: 'typescript', func: typescript },
    { name: 'scss', func: scss },
    { name: 'xml', func: xml },
    { name: 'json', func: json },
  ];
}

@NgModule({
  imports: [
    BrowserAnimationsModule,
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    HighlightModule,
    PartialsModule,
    OverlayModule,
    LayoutModule,
    NgxPermissionsModule.forRoot(),
    EmptyComponentComponent,
    AppComponent,
  ],
  providers: [
    AuthService,
    // Página aberta durante um deploy pede arquivo que já sumiu: recarrega uma vez.
    { provide: ErrorHandler, useClass: RecargaAposDeployErrorHandler },
    { provide: LOCALE_ID, useValue: 'pt' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
    LayoutConfigService,
    LayoutRefService,
    MenuConfigService,
    PageConfigService,
    DataTableService,
    SplashScreenService,
    {
      provide: MAT_FAB_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
      } as MatFabDefaultOptions,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [AuthService, LayoutConfigService],
      multi: true,
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        languages: getHighlightLanguages,
      },
    },
    // template services
    SubheaderService,
    MenuHorizontalService,
    MenuAsideService,
    HttpUtilsService,
    TypesUtilsService,
    LayoutUtilsService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      deps: [ApiService, NotificationService],
      multi: true,
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['snack'],
      },
    },
    // D-2 (dono, 31/08/2026): contornado. O preenchido pintava o container com
    // o papel `surface-variant` do M3 — origem do fundo lilás. Contornado não
    // tem container, então o defeito deixa de ser possível.
    // Portão: src/testing/__arquitetura__/aparencia-de-campo.pure.spec.ts
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    // Teto de viewport de TODO diálogo (dono, 05/09/2026). Espalha o
    // `MatDialogConfig` para não perder os padrões do Material (foco, backdrop,
    // `closeOnNavigation`): com um objeto provido, o `MatDialog.open` faz
    // `{ ...padrão, ...chamada }` e NÃO instancia o `MatDialogConfig` — o CDK
    // repõe os dele, mas `delayFocusTrap` é só do Material. Até 05/09 este
    // padrão era provido apenas no `AdministrativeModule` (`min(56rem, 92vw)`);
    // o resto do app abria sem teto nenhum. A largura vem da escala em cada
    // chamada: `@core/dialogo/largura-de-dialogo`.
    // Portão: src/testing/__arquitetura__/geometria-de-dialogo.pure.spec.ts
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { ...new MatDialogConfig(), maxWidth: TETO_DE_DIALOGO } },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // Rota bloqueada por permissão explica o desvio (o de plano avisa no `comModulo`).
    provideAvisoDeRotaBloqueada(),
  ],
})
export class AppModule implements DoBootstrap {
  /**
   * `AppComponent` virou STANDALONE na SU-04 (28/08/2026), e componente
   * standalone **não pode** entrar no array `bootstrap` de um `NgModule` — o
   * Angular reprova com "The `AppComponent` class is a standalone component,
   * which can not be used in the `@NgModule.bootstrap` array. Use the
   * `bootstrapApplication` function for bootstrap instead." O erro não sai só
   * em runtime: o `TestBed` o levanta ao COMPILAR o módulo, e derruba o
   * `__aceite__/grafo-app-module.spec.ts`.
   *
   * `bootstrapApplication` seria a saída do modo `standalone-bootstrap` do
   * schematic — mas ele apaga o `AppModule` inteiro, que é a `CV-CLI-16`, com
   * decisão de dono pendente. `ngDoBootstrap` é a ponte suportada: o módulo
   * continua de pé, e `ApplicationRef.bootstrap()` aceita componente
   * standalone. Quando a CV-CLI-16 for executada, isto e o `platformBrowserDynamic`
   * de `main.ts` saem juntos.
   */
  ngDoBootstrap(appRef: ApplicationRef): void {
    appRef.bootstrap(AppComponent);
  }

  constructor() {
    const injector = inject(Injector);

    // Preenche o service locator da FOLHA `./app-injector` — quem precisa dele
    // importa a folha, nunca este módulo: import do `app.module` fecha ciclo
    // com o grafo inteiro e já derrubou o QA (25/08/2026, provider `undefined`;
    // ver `src/testing/__aceite__/grafo-app-module.spec.ts`).
    setAppInjector(injector);

    // Task 19 (DV-07, 27/08/2026) — família única de ícone: todo `<mat-icon
    // svgIcon="nome">` do sistema resolve por AQUI, uma vez, global. Registro
    // por RESOLVER (não por sprite): medido em `t19-report.md` — o sprite
    // único pesa menos comprimido (15,17 KB contra 31,88 KB somando os 112
    // arquivos individuais comprimidos um a um), mas carrega os 112 ícones de
    // uma vez na primeira tela. O resolver busca só o que a tela usa, cacheia
    // por nome pelo resto da sessão (`MatIconRegistry` nunca refaz a
    // requisição do mesmo nome) e não pede nenhum passo de build novo: os
    // arquivos que `npm run icones` já gera são exatamente o que o resolver
    // consome. `bypassSecurityTrustResourceUrl` é obrigatório — sem ele o
    // Angular recusa a URL do SVG por sanitização.
    const matIconRegistry = inject(MatIconRegistry);
    const sanitizer = inject(DomSanitizer);
    matIconRegistry.addSvgIconResolver(nome => sanitizer.bypassSecurityTrustResourceUrl(`./assets/icones/${nome}.svg`));
  }
}
